import { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { registerSitlCopTourApi } from 'components/MapPage/sitlCopTour/sitlCopTourBridge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { useGroupGuid } from 'components/IAP/hooks/commonHooks';
import type { ExternalLayer } from 'hooks/externalLayer/types';
import type {
  InternalLayer,
  LayerWithNodeMeta,
} from 'hooks/internalLayer/types';
import type { LayerFolderItemDto, LayerGroupDto } from 'hooks/layerNode/types';
import { useListLayerNodes } from 'hooks/layerNode/useListLayerNodes';
import {
  usePowerOutagesFlag,
  useTennesseeNationalGuardFlag,
} from 'utils/featureFlags';
import { AddLayerFolderModal } from '../LayersTab/AddLayerFolderModal';
import { AddLayerPanel } from '../LayersTab/AddLayerPanel';
import { requestDraftLayerLeave } from '../LayersTab/draftLayerLeaveGuard';
import { DraftNewVersionPanel } from '../LayersTab/DraftNewVersionPanel';
import { EditLayerVersionPanel } from '../LayersTab/EditLayerVersionPanel';
import { LayerDetailModal } from '../LayersTab/LayerDetailModal';
import { MapLayersBackButton } from '../LayersTab/MapLayersBackButton';
import { useLayerSidebarPanel } from '../LayersTab/LayerSidebarPanel';
import { useUscgMapContext } from '../LayersTab/UscgMapContext';
import {
  isExternalLayer,
  isLayerItemInCurrentScope,
} from '../LayersTab/utils/isLayerItemInCurrentScope';
import {
  itemMatchesWorkspace,
  LayerWorkspaceFilterButton,
} from '../LayersTab/LayerWorkspaceFilterButton';
import {
  SitStatEriLayerItem,
  SitStatExternalLayerItem,
  SitStatInternalLayerItem,
  SitStatLayerFolderRow,
  SitStatPowerOutageLayerItem,
  SitStatTakLayerItem,
} from './SitStatLayerItems';

const isFolderItemVisible =
  (
    isInternalLayerVisible: (id: string) => boolean,
    isExternalLayerVisible: (id: string) => boolean
  ) =>
  (item: LayerFolderItemDto) => {
    const { layer } = item;
    return isExternalLayer(layer)
      ? isExternalLayerVisible(layer.id)
      : isInternalLayerVisible(layer.id);
  };

function areAllFolderItemsVisible(
  group: LayerGroupDto,
  isInternalLayerVisible: (id: string) => boolean,
  isExternalLayerVisible: (id: string) => boolean
) {
  const { items } = group;
  if (items.length === 0) return false;
  const checkVisible = isFolderItemVisible(
    isInternalLayerVisible,
    isExternalLayerVisible
  );
  return items.every(checkVisible);
}

function isFolderGroupIndeterminate(
  group: LayerGroupDto,
  isInternalLayerVisible: (id: string) => boolean,
  isExternalLayerVisible: (id: string) => boolean
) {
  const { items } = group;
  if (items.length === 0) return false;
  const checkVisible = isFolderItemVisible(
    isInternalLayerVisible,
    isExternalLayerVisible
  );
  const someVisible = items.some(checkVisible);
  const allVisible = items.every(checkVisible);
  return someVisible && !allVisible;
}

function handleFolderGroupVisibilityToggle(
  group: LayerGroupDto,
  isInternalLayerVisible: (id: string) => boolean,
  isExternalLayerVisible: (id: string) => boolean,
  toggleInternalLayerVisibility: (id: string) => void,
  toggleExternalLayerVisibility: (id: string) => void
) {
  const { items } = group;
  if (items.length === 0) return;

  const checkVisible = isFolderItemVisible(
    isInternalLayerVisible,
    isExternalLayerVisible
  );
  const allVisible = items.every(checkVisible);
  const targetVisible = !allVisible;

  items.forEach(({ layer }) => {
    const currentlyVisible = isExternalLayer(layer)
      ? isExternalLayerVisible(layer.id)
      : isInternalLayerVisible(layer.id);

    if (currentlyVisible !== targetVisible) {
      if (isExternalLayer(layer)) {
        toggleExternalLayerVisibility(layer.id);
      } else {
        toggleInternalLayerVisibility(layer.id);
      }
    }
  });
}

function toLayerWithNodeMeta(item: LayerFolderItemDto): LayerWithNodeMeta {
  return {
    ...(isExternalLayer(item.layer)
      ? {
          type: 'external' as const,
          layer: item.layer as ExternalLayer,
        }
      : {
          type: 'internal' as const,
          layer: item.layer as InternalLayer,
        }),
    ...item,
  };
}

const HOSTED_PANELS = [
  'add_dhs_layer',
  'add_default_layer',
  'add_external_layer',
  'add_internal_layer',
  'draft_new_version',
  'edit_layer_version',
] as const;

type HostedPanel = (typeof HOSTED_PANELS)[number];

function isHostedPanel(panel: string | null): panel is HostedPanel {
  return (HOSTED_PANELS as readonly (string | null)[]).includes(panel);
}

function hostedPanelTitle(panel: HostedPanel): string {
  switch (panel) {
    case 'add_dhs_layer':
      return 'Add from DHS';
    case 'add_default_layer':
      return 'Add Default Layer';
    case 'add_external_layer':
      return 'Add from ArcGIS';
    case 'add_internal_layer':
      return 'Add Internal Layer';
    case 'draft_new_version':
      return 'Draft New Version';
    case 'edit_layer_version':
      return 'Edit Layer Version';
  }
}

/**
 * The Layers tab swaps these panels into the sidebar in place of the layer
 * list. A SitStat rail is far too narrow for that, so they get a dialog.
 */
function SitStatLayerPanelDialog() {
  const { activePanel, setActivePanel } = useLayerSidebarPanel();
  const { setSidebarSelectedInternalLayer } = useUscgMapContext();

  const close = () => {
    setActivePanel(null);
    setSidebarSelectedInternalLayer(null);
  };

  if (!isHostedPanel(activePanel)) return null;

  const renderPanel = () => {
    switch (activePanel) {
      case 'add_dhs_layer':
        return <AddLayerPanel onBack={close} layerType="dhs" />;
      case 'add_default_layer':
        return <AddLayerPanel onBack={close} layerType="default" />;
      case 'add_external_layer':
        return <AddLayerPanel onBack={close} layerType="external" />;
      case 'add_internal_layer':
        return <AddLayerPanel onBack={close} layerType="internal" />;
      case 'draft_new_version':
        return <DraftNewVersionPanel onBack={close} />;
      case 'edit_layer_version':
        return <EditLayerVersionPanel onBack={close} />;
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (open) return;
        if (activePanel === 'add_internal_layer') {
          requestDraftLayerLeave(close);
          return;
        }
        close();
      }}
    >
      <DialogContent className="dark tw:flex tw:max-h-[88vh] tw:w-[min(56rem,calc(100vw-2rem))] tw:flex-col tw:gap-3 tw:overflow-hidden tw:p-5 tw:sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>{hostedPanelTitle(activePanel)}</DialogTitle>
        </DialogHeader>
        <div className="tw:min-h-0 tw:flex-1 tw:overflow-y-auto">
          {renderPanel()}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * SitStat Map Layers widget — same layer tree as Layers tab, with shadcn list
 * chrome and nested Layers-tab content for expand panels.
 */
export function SitStatMapLayersWidget() {
  const groupGuid = useGroupGuid();
  const tennesseeNationalGuardFlag = useTennesseeNationalGuardFlag();
  const powerOutagesFlag = usePowerOutagesFlag();
  const { activePanel, setActivePanel } = useLayerSidebarPanel();
  const {
    focusFeatureOnMap,
    focusLayerOnMap,
    incidentFilterId,
    isInternalLayerVisible,
    isExternalLayerVisible,
    toggleInternalLayerVisibility,
    toggleExternalLayerVisibility,
  } = useUscgMapContext();

  useEffect(() => {
    return registerSitlCopTourApi({
      openAddInternalLayer: () => setActivePanel('add_internal_layer'),
    });
  }, [setActivePanel]);

  const [layerDetailModal, setLayerDetailModal] =
    useState<LayerWithNodeMeta | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [workspaceFilterId, setWorkspaceFilterId] = useState<string | null>(
    null
  );
  const [focusedLayerId, setFocusedLayerId] = useState<string | null>(null);

  const { data: layerNodeGroups, isLoading } = useListLayerNodes({
    filter: {
      groupGuid: groupGuid ?? '',
      ...(incidentFilterId ? { incidentIds: [incidentFilterId] } : {}),
      ...(searchTerm.trim() ? { search: searchTerm.trim() } : {}),
    },
  });

  const { standaloneItems, folderGroups } = useMemo(() => {
    const groups = layerNodeGroups?.data ?? [];
    const standalone: LayerFolderItemDto[] = [];
    const folders: LayerGroupDto[] = [];

    for (const group of groups) {
      const scopedItems = group.items.filter(
        (item) =>
          isLayerItemInCurrentScope(item, incidentFilterId) &&
          itemMatchesWorkspace(item.layer, workspaceFilterId)
      );
      if (scopedItems.length === 0) continue;

      if (group.folder == null) {
        standalone.push(...scopedItems);
      } else {
        folders.push({ ...group, items: scopedItems });
      }
    }

    return { standaloneItems: standalone, folderGroups: folders };
  }, [layerNodeGroups?.data, incidentFilterId, workspaceFilterId]);

  const focusedItem = useMemo(() => {
    if (!focusedLayerId) return null;
    const items = [
      ...standaloneItems,
      ...folderGroups.flatMap((group) => group.items),
    ];
    return items.find((item) => item.layer.id === focusedLayerId) ?? null;
  }, [focusedLayerId, standaloneItems, folderGroups]);

  const renderLayerItem = (changeArg: LayerWithNodeMeta) => {
    const { layer, type } = changeArg;

    return type === 'internal' ? (
      <SitStatInternalLayerItem
        key={`internal-${layer.id}`}
        layer={layer}
        defaultOpen={focusedLayerId === layer.id}
        onFocusView={() => setFocusedLayerId(layer.id)}
        onZoomToLayer={() => {
          void focusLayerOnMap({
            kind: 'internal',
            layerId: layer.id,
            name: layer.name,
            lastUpdated: layer.updatedAt,
          });
        }}
      />
    ) : (
      <SitStatExternalLayerItem
        key={`external-${layer.id}`}
        layer={layer}
        onOpenInNewClick={() => setLayerDetailModal(changeArg)}
        onZoomToLayer={() => {
          void focusLayerOnMap({
            kind: 'external',
            layerId: layer.id,
            name: layer.name,
            lastUpdated: layer.updatedAt,
          });
        }}
        onZoomToGraphic={(graphic) => {
          void focusFeatureOnMap({
            graphic,
            layer: { kind: 'external', layerId: layer.id },
          });
        }}
      />
    );
  };

  const isEmpty =
    !tennesseeNationalGuardFlag &&
    !powerOutagesFlag &&
    standaloneItems.length === 0 &&
    folderGroups.length === 0;

  if (focusedLayerId) {
    return (
      <div className="tw:max-h-80 tw:space-y-2 tw:overflow-y-auto tw:pr-0.5">
        <MapLayersBackButton onClick={() => setFocusedLayerId(null)} />
        {focusedItem ? (
          renderLayerItem(toLayerWithNodeMeta(focusedItem))
        ) : (
          <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
            This layer is no longer in the list.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="tw:max-h-80 tw:space-y-2 tw:overflow-y-auto tw:pr-0.5">
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-1.5">
        <div className="tw:relative tw:min-w-[8rem] tw:flex-1">
          <Search className="tw:pointer-events-none tw:absolute tw:left-2 tw:top-1/2 tw:h-3.5 tw:w-3.5 tw:-translate-y-1/2 tw:text-muted-foreground" />
          <Input
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search layers…"
            className="tw:h-8 tw:pl-8 tw:text-xs"
            aria-label="Search map layers"
          />
        </div>
        <LayerWorkspaceFilterButton
          value={workspaceFilterId}
          onChange={setWorkspaceFilterId}
        />
        <AddLayerFolderModal variant="shadcn" />
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button
                type="button"
                size="sm"
                className="tw:shrink-0"
                data-tour="sitl-cop-add-layer-menu"
              >
                + Add Layer
              </Button>
            }
          />
          <DropdownMenuContent align="end" className="dark tw:w-52">
            <DropdownMenuItem
              onClick={() => setActivePanel('add_external_layer')}
            >
              Add from ArcGIS
            </DropdownMenuItem>
            <DropdownMenuItem
              data-tour="sitl-cop-add-internal-layer"
              onClick={() => setActivePanel('add_internal_layer')}
            >
              Add Internal Layer
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {isLoading ? (
        <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
          Loading layers…
        </p>
      ) : isEmpty ? (
        <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
          {searchTerm.trim()
            ? 'No layers match this search.'
            : workspaceFilterId
              ? 'No layers match this workspace.'
              : 'No layers for this Hub yet.'}
        </p>
      ) : (
        <>
          {tennesseeNationalGuardFlag ? <SitStatTakLayerItem /> : null}
          {powerOutagesFlag ? (
            <>
              <SitStatPowerOutageLayerItem />
              <SitStatEriLayerItem />
            </>
          ) : null}

          {standaloneItems.map((item) =>
            renderLayerItem(toLayerWithNodeMeta(item))
          )}

          {folderGroups.map((group) => {
            const folderLabel = group.folder!.name;
            const folderId = group.folder!.id;

            return (
              <SitStatLayerFolderRow
                key={`sitstat-folder-${folderId}`}
                label={folderLabel}
                checked={areAllFolderItemsVisible(
                  group,
                  isInternalLayerVisible,
                  isExternalLayerVisible
                )}
                indeterminate={isFolderGroupIndeterminate(
                  group,
                  isInternalLayerVisible,
                  isExternalLayerVisible
                )}
                onCheckedChange={() =>
                  handleFolderGroupVisibilityToggle(
                    group,
                    isInternalLayerVisible,
                    isExternalLayerVisible,
                    toggleInternalLayerVisibility,
                    toggleExternalLayerVisibility
                  )
                }
                defaultOpen={group.items.length > 0}
              >
                {group.items.length === 0 ? (
                  <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                    No layers
                  </p>
                ) : (
                  group.items.map((item) =>
                    renderLayerItem(toLayerWithNodeMeta(item))
                  )
                )}
              </SitStatLayerFolderRow>
            );
          })}
        </>
      )}

      {/* The detail modal is a Bootstrap modal, which outranks the shadcn
          dialog on z-index, so it steps aside while a panel is open. */}
      <LayerDetailModal
        key={layerDetailModal?.layer.id ?? 'no-layer'}
        show={layerDetailModal !== null && !isHostedPanel(activePanel)}
        onClose={() => setLayerDetailModal(null)}
        layerWithType={layerDetailModal}
      />

      <SitStatLayerPanelDialog />
    </div>
  );
}
